import { useEffect, useState } from "react";
import { Mic, Users, Briefcase, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import AppShell from "../components/Layout/AppShell";
import { DashboardHeroStart } from "../components/Recording/StartInterviewButton";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";

const STROKE = 1.5;

function StatTile({ label, value, hint, testId }) {
  return (
    <div className="hp-card p-5" data-testid={testId}>
      <div className="hp-section-label">{label}</div>
      <div className="font-display mt-2 text-3xl font-medium text-ink-900">{value}</div>
      <div className="mt-1 text-[12px] text-ink-500">{hint}</div>
    </div>
  );
}

export default function Dashboard() {
  const { workspace, member, user } = useAuth();
  const [interviewCount, setInterviewCount] = useState(null);
  const [candidateCount, setCandidateCount] = useState(null);
  const [roleCount, setRoleCount] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/interviews").then((r) => r.data.length).catch(() => 0),
      api.get("/candidates").then((r) => r.data.length).catch(() => 0),
      api.get("/roles").then((r) => r.data.length).catch(() => 0),
    ]).then(([i, c, r]) => {
      setInterviewCount(i);
      setCandidateCount(c);
      setRoleCount(r);
    });
  }, []);

  const noInterviews = interviewCount === 0;

  return (
    <AppShell>
      <div data-testid="dashboard-page" className="space-y-10">
        <header>
          <div className="hp-section-label">Dashboard</div>
          <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
            Welcome, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="mt-1 text-[14px] text-ink-500">
            {workspace?.name} · {member?.role?.replace("_", " ")} access
          </p>
        </header>

        {noInterviews ? (
          <section
            className="hp-card relative overflow-hidden p-10 lg:p-14"
            data-testid="dashboard-empty-hero"
          >
            <div className="hp-grid-bg absolute inset-0 opacity-50" aria-hidden="true" />
            <div className="relative max-w-2xl">
              <Mic className="h-6 w-6 text-emerald-800" strokeWidth={STROKE} />
              <h2 className="font-display mt-5 text-3xl font-medium leading-tight text-ink-900 sm:text-4xl">
                Record your first interview.
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-600">
                Capture consent, record in person or online, and produce a defensible transcript + interview-format summary in under a minute. This is the start of your interview record.
              </p>
              <div className="mt-7">
                <DashboardHeroStart />
              </div>
              <div className="mt-5 flex items-center gap-2 text-[12px] text-ink-500">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-800" strokeWidth={STROKE} />
                Consent is required before recording starts — no bypass.
              </div>
            </div>
          </section>
        ) : (
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Interviews recorded" value={interviewCount ?? "—"} hint="Total across team" testId="stat-interviews" />
            <StatTile label="Candidates" value={candidateCount ?? "—"} hint="In the pipeline" testId="stat-candidates" />
            <StatTile label="Open roles" value={roleCount ?? "—"} hint="Hiring active" testId="stat-roles" />
            <StatTile
              label="Free trial"
              value={workspace?.plan === "free" ? `${Math.max(0, 2 - (workspace?.free_interviews_used || 0))} / 2` : "—"}
              hint={workspace?.plan === "free" ? "Free interviews left" : "On Team plan"}
              testId="stat-trial"
            />
          </section>
        )}

        {!noInterviews && (
          <section>
            <div className="hp-section-label">Quick links</div>
            <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 md:grid-cols-3">
              {[
                { icon: Users, title: "Candidates", to: "/candidates", body: "Aggregated candidate pipeline.", testid: "tile-candidates" },
                { icon: Briefcase, title: "Roles", to: "/roles", body: "Open roles & interviewer assignments.", testid: "tile-roles" },
                { icon: Users, title: "Team", to: "/team", body: "Invites and role permissions.", testid: "tile-team" },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <Link key={c.title} to={c.to} className="group bg-white p-6 transition hover:bg-ink-50" data-testid={c.testid}>
                    <Icon className="h-5 w-5 text-emerald-800" strokeWidth={STROKE} />
                    <h3 className="font-display mt-4 text-lg font-medium text-ink-900">{c.title}</h3>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-ink-600">{c.body}</p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
